import React from "react";
import { useTranslation } from "react-i18next";
import {
  MeFlightAlertItem,
  MeFlightAlertMoment,
  MeFlightAlertState,
} from "../../api/meFlightAlerts.service";
import { formatLastUpdatedHKT } from "./handler/badge.helper";

export interface AlertListRowProps {
  alert: MeFlightAlertItem;
  /** Omitted (not just hidden) for a non-OPEN alert -- stop only ever applies to an open one. */
  onStopClick?: (alert: MeFlightAlertItem) => void;
}

// STOPPED must read as visually distinct from CLOSED wherever state is shown (DASH-6346 Spec
// I-1) -- same reasoning as the admin closed-alerts table, applied here with this screen's own
// token palette (BadgePill.tsx's flight-*/pending-* tokens) rather than MUI's warning/default.
const STATE_PILL_CLASS: Record<MeFlightAlertState, string> = {
  OPEN: "bg-primary-tint text-brand-blue",
  CLOSED: "bg-flight-neutral-bg text-flight-neutral",
  STOPPED: "bg-pending-tint text-pending-ink",
};

const STATE_LABEL_KEY: Record<MeFlightAlertState, string> = {
  OPEN: "alertList.state.open",
  CLOSED: "alertList.state.closed",
  STOPPED: "alertList.state.stopped",
};

// Reuses the flight-lookup results screen's own badge copy wherever a moment has a direct
// equivalent (boarding/finalCall/landed/departed/cancelled), rather than duplicating it -- only
// "watching"/"moved"/"noLongerListed" have no equivalent there and are new to this screen.
const MOMENT_LABEL_KEY: Record<MeFlightAlertMoment, string> = {
  watching: "alertList.moment.watching",
  moved: "alertList.moment.moved",
  boarding: "results.badgeBoarding",
  finalCall: "results.badgeFinalCall",
  landed: "results.badgeLanded",
  departed: "results.badgeDeparted",
  cancelled: "results.badgeCancelled",
  noLongerListed: "alertList.moment.noLongerListed",
};

export const AlertListRow: React.FC<AlertListRowProps> = ({ alert, onStopClick }) => {
  const { t } = useTranslation("flightAlerts");
  const lastChecked = formatLastUpdatedHKT(alert.lastCheckedAt);
  const estimate = formatLastUpdatedHKT(alert.lastMovedEstimateUtc);

  return (
    // Same card spec as the flight-lookup results screen's own cards (ArrivalResultCard.tsx /
    // DepartureResultCard.tsx: rounded-[16px] border-2 border-ink-12 bg-card p-[32px] gap-[24px])
    // -- this row previously used a tighter p-5/gap-3 of its own, which read as visibly denser
    // than the rest of the app (Roy Chan, 2026-09-24: no Figma for Phase 2 exists, "mimic and use
    // existing screen as design system").
    <div className="flex flex-col gap-[24px] rounded-[16px] border-2 border-ink-12 bg-card p-[32px]">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-2">
          <span className="font-lexend text-[18px]/[22px] font-bold tracking-[-0.4px] text-ink-100">
            {alert.operatingDesignator}
          </span>
          {/* Same chip treatment as the flight-lookup results screen's own direction badge
              (ArrivalResultCard.tsx/DepartureResultCard.tsx: bg-ink-100+flight_land for arrival,
              bg-primary-100+flight_takeoff for departure) -- reused here rather than a new style,
              now also carrying the date. */}
          <span
            className={`inline-flex w-fit shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full py-[5px] pl-[10px] pr-[10px] ${
              alert.direction === "ARRIVAL" ? "bg-ink-100" : "bg-primary-100"
            }`}
          >
            <span className="font-material-symbols text-[14px] leading-none text-white">
              {alert.direction === "ARRIVAL" ? "flight_land" : "flight_takeoff"}
            </span>
            <span className="font-lexend text-[10px]/[12px] font-bold tracking-[0.6px] uppercase text-white">
              {alert.direction === "ARRIVAL"
                ? t("alertList.directionArrival")
                : t("alertList.directionDeparture")}
              {" · "}
              {alert.flightDate}
            </span>
          </span>
        </div>
        <span
          className={`inline-flex shrink-0 items-center rounded-full px-3 py-1 font-lexend text-[12px]/[16px] font-bold tracking-normal ${STATE_PILL_CLASS[alert.state]}`}
        >
          {t(STATE_LABEL_KEY[alert.state])}
        </span>
      </div>

      <div className="h-px w-full bg-ink-10" />

      <div className="flex flex-col gap-1">
        <span className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-100">
          {t(MOMENT_LABEL_KEY[alert.lastKnownMoment])}
          {estimate ? ` · ${t("alertList.estimate", { time: estimate })}` : ""}
        </span>
        {lastChecked && (
          <span className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-40">
            {t("results.lastUpdated", { time: lastChecked })}
          </span>
        )}
      </div>

      {alert.state === "OPEN" && onStopClick && (
        <button
          type="button"
          onClick={() => onStopClick(alert)}
          className="w-fit font-lexend text-[13px]/[16px] font-bold tracking-normal text-flight-red"
        >
          {t("alertList.stopAction")}
        </button>
      )}
    </div>
  );
};
